// src/components/map/MapExplorationView.tsx
//
// Top-half panel for a map_exploration conversation. Mirrors the
// drawer/snap-point pattern from WalkingTourView so the UI feels
// consistent across all "map-on-top" modes:
//
//   ┌─────────────────────────┐
//   │      MAP (50vh)         │  ← user dot, place pins, my-pins overlay
//   │   (drag bar below)      │
//   ├─────────────────────────┤
//   │ ≡ pull-bar              │  ← drag to peek (8vh) / default (50vh) / expanded (80vh)
//   │ "ramen near me" · 9     │  ← query summary + count
//   │ [3.0+] [Open now]       │  ← filter chips
//   ├─────────────────────────┤
//   │       (chat below)      │  ← parent renders chat under us
//   └─────────────────────────┘
//
// State machine maps to status:
//   generating → place pins absent; centered loader card on map
//   ready      → place pins rendered; chips active
//   error      → error banner with retry; previous pins stay if any
//
// Pin tap opens a small bottom Drawer with name / rating / blurb /
// distance + "Pin to my map" + "Take me there" actions. The full
// FeedItem-style detail sheet with photos plural + reviews comes
// in Phase 4; the mini sheet is enough to act on a place.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  Stack,
  CircularProgress,
  Button,
  Fab,
  Chip,
  Alert,
} from '@mui/material';
import {
  MyLocationOutlined,
  CloseOutlined,
  PushPinOutlined,
  PushPin,
  DirectionsWalkOutlined,
  StarRounded,
  SearchOutlined,
  ErrorOutlineOutlined,
  RefreshOutlined,
  AccessTimeFilled,
  DoNotDisturbAltOutlined,
} from '@mui/icons-material';
import {
  Map as GMap,
  AdvancedMarker,
  useMap,
  useApiIsLoaded,
} from '@vis.gl/react-google-maps';
import { useLocation } from '../../hooks/useLocation';
import { usePinned, usePinActions, useIsPinned } from '../../hooks/usePinned';
import { UserLocationMarker } from './UserLocationMarker';
import { MapPin, MyPinDot } from './MapPin';
import { MapFilterBar } from './MapFilterBar';
import { isOpenNow } from '../../utils/openHours';
import type {
  Conversation,
  MapExplorationPlace,
} from '../../stores/conversationStore';
import { useT } from '../../i18n/useT';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

// ── Shared math helper ───────────────────────────────────────────

/** Great-circle distance in meters. Identical to the haversineM
 *  in WalkingTourView and the haversineKm in MapPage — eventually
 *  worth consolidating into a util. Kept inlined here so this
 *  component is self-contained and Phase 1C touches one fewer
 *  file. */
function haversineM(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** Format meters for the place detail sheet — m below 1km, km
 *  with one decimal otherwise. We don't bother with mi here for
 *  Phase 1C; Phase 2 can wire formatDistance from
 *  useUserPreferences when the settings UI lands. */
function formatMeters(m: number): string {
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

/** Rough walk time at 5 km/h. Used as a quick "tilde-time" hint
 *  on the detail sheet. We don't do a Distance Matrix call until
 *  the user actually taps "Take me there" — saves one API call
 *  per pin tap, which is the high-frequency action on this view. */
function tildeWalkMinutes(meters: number): number {
  return Math.max(1, Math.round(meters / 83.33)); // ~5 km/h
}

// ── Small in-component helpers (live inside <Map>) ───────────────

/** One-shot pan/zoom command via the imperative useMap handle.
 *  Set `recenterTo` to trigger; the controller calls onConsumed
 *  immediately so the parent can null it back out. */
const RecenterController: React.FC<{
  recenterTo: { lat: number; lng: number; zoom?: number } | null;
  onConsumed: () => void;
}> = ({ recenterTo, onConsumed }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !recenterTo) return;
    map.panTo({ lat: recenterTo.lat, lng: recenterTo.lng });
    if (recenterTo.zoom !== undefined) {
      map.setZoom(recenterTo.zoom);
    }
    onConsumed();
  }, [map, recenterTo, onConsumed]);
  return null;
};

/** Auto-fit bounds to the user + the visible places. Re-fits when
 *  the place id-set changes (e.g. on regenerate / refinement) but
 *  not on every snapshot churn — keyed on a stable signature. */
const PlacesBoundsFitter: React.FC<{
  user: { lat: number; lng: number } | null;
  places: MapExplorationPlace[];
  signature: string;
}> = ({ user, places, signature }) => {
  const map = useMap();
  const isLoaded = useApiIsLoaded();
  const fittedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!map || !isLoaded) return;
    if (fittedFor.current === signature) return;
    if (places.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    if (user) bounds.extend(user);
    for (const p of places) bounds.extend({ lat: p.lat, lng: p.lng });
    map.fitBounds(bounds, { top: 80, right: 60, bottom: 80, left: 60 });
    fittedFor.current = signature;
  }, [map, isLoaded, signature, places, user]);

  return null;
};

// ── Drawer snap configuration ────────────────────────────────────
//
// Same dimensions as WalkingTourView so the two modes feel
// physically identical when a user toggles between them.

const SNAP_PEEK_VH = 8;
const SNAP_DEFAULT_VH = 50;
const SNAP_EXPANDED_VH = 80;
type SnapPoint = 'peek' | 'default' | 'expanded';

// ── Main view ────────────────────────────────────────────────────

interface MapExplorationViewProps {
  conversation: Conversation;
}

export const MapExplorationView: React.FC<MapExplorationViewProps> = ({
  conversation,
}) => {
  const t = useT();
  const me = conversation.map_exploration;
  const { location } = useLocation();
  const { pinned } = usePinned();
  const { pin, unpin } = usePinActions();

  const user = useMemo(
    () =>
      location?.latitude && location?.longitude
        ? { lat: location.latitude, lng: location.longitude }
        : null,
    [location?.latitude, location?.longitude]
  );

  // ── Filter state ───────────────────────────────────────────
  // Source of truth is the conversation doc's `filters` field. We
  // mirror it into local state for optimistic UI (chip tap →
  // immediate visual update without waiting for the Firestore
  // round-trip) and write any user-driven change back. The AI's
  // CONVERSATION_STATE marker can ALSO write filters (when the
  // user says "narrow to 4 stars" in chat); the snapshot listener
  // syncs that back into local state via the useEffect below.
  //
  // Snapshot mapper falls back to defaults (3.0 / false) when
  // me.filters is undefined, so we always have valid initial
  // values.
  const [minRating, setMinRating] = useState<number>(
    () => me?.filters?.min_rating ?? 3.0
  );
  const [openNow, setOpenNow] = useState<boolean>(
    () => me?.filters?.open_now ?? false
  );

  // Sync local filter state when the snapshot's filters change.
  // We compare by value and skip the setState if equal — same
  // value setState is a no-op but the comparison is cheaper than
  // letting React diff unnecessarily, and it's clearer to read.
  useEffect(() => {
    const next = me?.filters?.min_rating;
    if (typeof next === 'number' && next !== minRating) {
      setMinRating(next);
    }
    // We don't depend on minRating here — that would ping-pong
    // local writes. The intent is "snapshot is the source of
    // truth," so we only react to me.filters changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.filters?.min_rating]);
  useEffect(() => {
    const next = me?.filters?.open_now === true;
    if (next !== openNow) {
      setOpenNow(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.filters?.open_now]);

  // Write a filter patch back to the conversation doc. Used by
  // the chip tap handlers — optimistic local update happens in
  // the setter; this just persists so a refresh / second device
  // sees the same state. Failures are logged but non-fatal — the
  // user's local UI stays as they left it; their next interaction
  // re-attempts the write.
  const persistFilterPatch = useCallback(
    (patch: Record<string, unknown>) => {
      if (!conversation?.id) return;
      void updateDoc(doc(db, 'conversations', conversation.id), {
        ...Object.fromEntries(
          Object.entries(patch).map(([k, v]) => [
            `map_exploration.filters.${k}`,
            v,
          ])
        ),
      }).catch((err) => {
        console.warn('persistFilterPatch failed:', err);
      });
    },
    [conversation?.id]
  );

  const handleMinRatingChange = useCallback(
    (value: number) => {
      setMinRating(value); // optimistic
      persistFilterPatch({ min_rating: value });
    },
    [persistFilterPatch]
  );
  const handleOpenNowChange = useCallback(
    (value: boolean) => {
      setOpenNow(value); // optimistic
      persistFilterPatch({ open_now: value });
    },
    [persistFilterPatch]
  );

  // ── Selected pin (controls the bottom detail sheet) ──────
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(
    null
  );

  // ── Drawer drag state ────────────────────────────────────────
  // Mirrors WalkingTourView's pattern verbatim. Three snaps,
  // pointer-drag on the header strip. Tap = cycle. See
  // WalkingTourView.tsx for the original prose.
  const [snapPoint, setSnapPoint] = useState<SnapPoint>('default');
  const [mapHeightVh, setMapHeightVh] = useState<number>(SNAP_DEFAULT_VH);
  const [dragging, setDragging] = useState(false);
  const dragStartYRef = useRef<number | null>(null);
  const dragStartVhRef = useRef<number>(SNAP_DEFAULT_VH);

  const heightVhFromSnap = useCallback((s: SnapPoint): number => {
    return s === 'peek'
      ? SNAP_PEEK_VH
      : s === 'expanded'
      ? SNAP_EXPANDED_VH
      : SNAP_DEFAULT_VH;
  }, []);

  const nearestSnap = useCallback((vh: number): SnapPoint => {
    const dPeek = Math.abs(vh - SNAP_PEEK_VH);
    const dDefault = Math.abs(vh - SNAP_DEFAULT_VH);
    const dExpanded = Math.abs(vh - SNAP_EXPANDED_VH);
    const min = Math.min(dPeek, dDefault, dExpanded);
    if (min === dPeek) return 'peek';
    if (min === dExpanded) return 'expanded';
    return 'default';
  }, []);

  const handleDragPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      const target = e.target as HTMLElement;
      const interactive = target.closest(
        'button, a, [role="button"], input, [data-no-drag]'
      );
      if (interactive) return;
      target.setPointerCapture(e.pointerId);
      dragStartYRef.current = e.clientY;
      dragStartVhRef.current = mapHeightVh;
      setDragging(true);
    },
    [mapHeightVh]
  );

  const handleDragPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (dragStartYRef.current === null) return;
      const deltaPx = e.clientY - dragStartYRef.current;
      const vhPerPx = 100 / window.innerHeight;
      const proposed = dragStartVhRef.current + deltaPx * vhPerPx;
      const clamped = Math.max(
        SNAP_PEEK_VH,
        Math.min(SNAP_EXPANDED_VH, proposed)
      );
      setMapHeightVh(clamped);
    },
    []
  );

  const handleDragPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (dragStartYRef.current === null) return;
      try {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        /* already released — fine */
      }
      const dy = Math.abs(e.clientY - dragStartYRef.current);
      let target: SnapPoint;
      if (dy < 4) {
        target =
          snapPoint === 'default'
            ? 'peek'
            : snapPoint === 'peek'
            ? 'expanded'
            : 'default';
      } else {
        target = nearestSnap(mapHeightVh);
      }
      setSnapPoint(target);
      setMapHeightVh(heightVhFromSnap(target));
      dragStartYRef.current = null;
      setDragging(false);
    },
    [mapHeightVh, snapPoint, nearestSnap, heightVhFromSnap]
  );

  // Keep mapHeightVh synced when snapPoint changes via other paths.
  useEffect(() => {
    if (!dragging) {
      setMapHeightVh(heightVhFromSnap(snapPoint));
    }
  }, [snapPoint, dragging, heightVhFromSnap]);

  // ── Recenter command ────────────────────────────────────────
  const [recenterCmd, setRecenterCmd] = useState<{
    lat: number;
    lng: number;
    zoom?: number;
  } | null>(null);

  // ── Filter the place list locally ────────────────────────────
  // The server has already pre-filtered by user-pref floors and
  // ranked. The client-side filters here apply on top of that
  // for the user's interactive tweaks (min_rating chip cycle,
  // open-now toggle). Cheap to compute every render — N ≤ 12.
  const places = useMemo(() => me?.places || [], [me?.places]);

  const visiblePlaces = useMemo(() => {
    let out = places;
    if (minRating > 0) {
      out = out.filter(
        (p) => typeof p.rating === 'number' && p.rating >= minRating
      );
    }
    if (openNow) {
      out = out.filter(
        (p) => isOpenNow(p.opening_hours_periods) === true
      );
    }
    return out;
  }, [places, minRating, openNow]);

  // Stable signature for the bounds fitter — keys on the visible
  // place id set + their coordinates. When the user toggles a
  // filter the visible set changes, and we want to re-fit so the
  // remaining pins are framed properly.
  const placesSignature = useMemo(
    () =>
      visiblePlaces
        .map((p) => `${p.place_id}@${p.lat.toFixed(4)},${p.lng.toFixed(4)}`)
        .join('|'),
    [visiblePlaces]
  );

  const selectedPlace = useMemo(
    () =>
      selectedPlaceId
        ? visiblePlaces.find((p) => p.place_id === selectedPlaceId) || null
        : null,
    [visiblePlaces, selectedPlaceId]
  );

  // ── My-pins overlay ───────────────────────────────────────────
  // Pinned items that are NOT in the active layer (we don't want
  // to render two markers at the same point). Rendered smaller
  // and muted so the active layer stays primary.
  const overlayPins = useMemo(() => {
    if (!pinned || pinned.length === 0) return [];
    const activePlaceIds = new Set(visiblePlaces.map((p) => p.place_id));
    return pinned.filter((p) => {
      // Drop overlay rendering when this same place is in the
      // active layer — avoids stacked markers at one point.
      if (p.place_id && activePlaceIds.has(p.place_id)) return false;
      return (
        typeof p.location?.lat === 'number' &&
        typeof p.location?.lng === 'number'
      );
    });
  }, [pinned, visiblePlaces]);

  // ── mapId / center ────────────────────────────────────────────
  const mapId =
    (import.meta as unknown as { env?: Record<string, string> }).env
      ?.VITE_GOOGLE_MAPS_MAP_ID || undefined;

  const initialCenter = user || { lat: 0, lng: 0 };

  const status = me?.status;

  return (
    <>
      {/* Top-half map. Drag-bar below controls height. */}
      <Box
        sx={{
          height: `${mapHeightVh}vh`,
          width: '100%',
          position: 'relative',
          zIndex: 10,
          flexShrink: 0,
          bgcolor: 'action.hover',
          transition: dragging
            ? 'none'
            : 'height 280ms cubic-bezier(0.32, 0.72, 0, 1)',
        }}
      >
        <GMap
          defaultCenter={initialCenter}
          defaultZoom={14}
          mapId={mapId}
          gestureHandling="greedy"
          disableDefaultUI={true}
          clickableIcons={false}
          keyboardShortcuts={false}
          style={{ width: '100%', height: '100%' }}
        >
          <RecenterController
            recenterTo={recenterCmd}
            onConsumed={() => setRecenterCmd(null)}
          />
          <PlacesBoundsFitter
            user={user}
            places={visiblePlaces}
            signature={placesSignature}
          />

          {/* User dot — always rendered when location available. */}
          {user && (
            <AdvancedMarker position={user}>
              <UserLocationMarker size={18} />
            </AdvancedMarker>
          )}

          {/* My-pins overlay (subtle). Underneath active layer. */}
          {overlayPins.map((p) => (
            <AdvancedMarker
              key={`mypin-${p.id}`}
              position={{ lat: p.location.lat, lng: p.location.lng }}
              // We don't make these clickable in 1C — clicking
              // routes to the existing pin detail sheet on the
              // Map tab, not here. Phase 4 unifies these.
              zIndex={1}
            >
              <MyPinDot />
            </AdvancedMarker>
          ))}

          {/* Active layer pins. */}
          {visiblePlaces.map((p) => {
            const isSelected = p.place_id === selectedPlaceId;
            return (
              <AdvancedMarker
                key={p.place_id}
                position={{ lat: p.lat, lng: p.lng }}
                zIndex={isSelected ? 100 : 10}
                onClick={() => setSelectedPlaceId(p.place_id)}
              >
                <MapPin
                  placeId={p.place_id}
                  photoRef={p.photo_ref}
                  active={isSelected}
                />
              </AdvancedMarker>
            );
          })}
        </GMap>

        {/* Recenter button (bottom-right of map). Hidden when the
            map is at peek height — the FAB would be off-screen
            anyway and click-throughs to the chat below are
            distracting. */}
        {user && mapHeightVh > SNAP_PEEK_VH + 4 && (
          <Fab
            size="small"
            color="default"
            onClick={() =>
              setRecenterCmd({ lat: user.lat, lng: user.lng, zoom: 15 })
            }
            sx={{
              position: 'absolute',
              right: 12,
              bottom: 12,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'background.paper' },
              zIndex: 5,
            }}
            aria-label={t('Recenter on my location')}
          >
            <MyLocationOutlined fontSize="small" />
          </Fab>
        )}

        {/* Generating overlay — shown only when status is
            'generating' AND we don't have any prior places to show.
            If we DO have prior places (refinement re-run in Phase
            2), keep the existing pins visible and show a smaller
            "updating" chip instead. */}
        {status === 'generating' && places.length === 0 && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 4,
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.96)',
                backdropFilter: 'blur(6px)',
                borderRadius: 999,
                px: 2,
                py: 1.25,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <CircularProgress size={16} thickness={5} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, fontSize: '0.78rem' }}
              >
                {t('Finding places…')}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Smaller "updating" chip when we have places + status is
            still generating (refinement). Phase 2 path; defensive
            for now. */}
        {status === 'generating' && places.length > 0 && (
          <Chip
            icon={<CircularProgress size={12} thickness={5} />}
            label={t('Updating…')}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              bgcolor: 'rgba(255,255,255,0.96)',
              backdropFilter: 'blur(6px)',
              fontWeight: 600,
              zIndex: 4,
              '& .MuiChip-icon': { ml: 1 },
            }}
          />
        )}

        {/* Error banner — top of map, dismissable. */}
        {status === 'error' && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              right: 8,
              zIndex: 4,
            }}
          >
            <Alert
              severity="error"
              icon={<ErrorOutlineOutlined />}
              action={
                me?.last_query ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      // Flip back to 'generating' with the same
                      // query. The trigger effect in ChatInterface
                      // releases the dedup key on error, so this
                      // re-fires createMapExploration.
                      void updateDoc(
                        doc(db, 'conversations', conversation.id),
                        {
                          'map_exploration.status': 'generating',
                          'map_exploration.last_error': '',
                        }
                      ).catch((err) =>
                        console.warn(
                          'map_exploration retry write failed:',
                          err
                        )
                      );
                    }}
                  >
                    {t('Retry')}
                  </Button>
                ) : undefined
              }
              sx={{
                bgcolor: 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(6px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '& .MuiAlert-message': { fontSize: '0.78rem' },
              }}
            >
              {me?.last_error
                ? me.last_error.length > 120
                  ? `${me.last_error.slice(0, 120)}…`
                  : me.last_error
                : t("Couldn't load places. Try again from chat.")}
            </Alert>
          </Box>
        )}
      </Box>

      {/* Drag-handle / query-summary / filter chips strip. The
          whole bar is the drag target except for interactive
          children (chips, buttons) which short-circuit the
          drag-down via the closest-interactive guard in
          handleDragPointerDown. */}
      <Box
        onPointerDown={handleDragPointerDown}
        onPointerMove={handleDragPointerMove}
        onPointerUp={handleDragPointerUp}
        onPointerCancel={handleDragPointerUp}
        sx={{
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          touchAction: 'none',
          cursor: dragging ? 'grabbing' : 'grab',
          position: 'relative',
          zIndex: 10,
          pt: 1.5,
          pb: 1,
          px: 2,
          userSelect: dragging ? 'none' : 'auto',
        }}
      >
        {/* Grabber pill. */}
        <Box
          sx={{
            position: 'absolute',
            top: 6,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: 'action.disabled',
            opacity: dragging ? 0.7 : 0.45,
            pointerEvents: 'none',
          }}
        />

        {/* Query summary line: search icon + last_query + count. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 0.25,
            mb: 0.75,
            minWidth: 0,
          }}
        >
          <SearchOutlined
            sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }}
          />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              flex: 1,
              minWidth: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {me?.last_query || t('Map exploration')}
          </Typography>
          {status === 'ready' && (
            <Chip
              size="small"
              label={
                visiblePlaces.length === places.length
                  ? `${places.length}`
                  : `${visiblePlaces.length} / ${places.length}`
              }
              sx={{
                height: 22,
                fontSize: '0.72rem',
                fontWeight: 700,
                flexShrink: 0,
              }}
            />
          )}
        </Box>

        {/* Filter chips. Disabled while generating with no prior
            places — there's nothing to filter against. */}
        <MapFilterBar
          minRating={minRating}
          openNow={openNow}
          onMinRatingChange={handleMinRatingChange}
          onOpenNowChange={handleOpenNowChange}
          disabled={status === 'generating' && places.length === 0}
        />
      </Box>

      {/* Pin detail mini-sheet. Bottom drawer, opens on pin tap. */}
      <Drawer
        anchor="bottom"
        open={!!selectedPlace}
        onClose={() => setSelectedPlaceId(null)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '70vh',
          },
        }}
      >
        {selectedPlace && (
          <PlaceDetailMini
            place={selectedPlace}
            user={user}
            onClose={() => setSelectedPlaceId(null)}
            onPin={async (toPin) => {
              if (toPin) {
                await pin({
                  id: selectedPlace.place_id,
                  title: selectedPlace.name,
                  description: selectedPlace.blurb || undefined,
                  // Photo URL is optional; we leave it null so the
                  // pin doc stays light. The Map tab can render
                  // the pin without a thumbnail. (Phase 4 may
                  // populate this for the unified pin detail sheet.)
                  image_url: null,
                  location: {
                    lat: selectedPlace.lat,
                    lng: selectedPlace.lng,
                  },
                  place_id: selectedPlace.place_id,
                  source: 'other',
                });
              } else {
                await unpin(selectedPlace.place_id);
              }
            }}
          />
        )}
      </Drawer>
    </>
  );
};

// ── Place detail mini-sheet ──────────────────────────────────────

const PlaceDetailMini: React.FC<{
  place: MapExplorationPlace;
  user: { lat: number; lng: number } | null;
  onClose: () => void;
  onPin: (toPin: boolean) => Promise<void>;
}> = ({ place, user, onClose, onPin }) => {
  const t = useT();
  const isPinned = useIsPinned(place.place_id);
  const [pinning, setPinning] = useState(false);

  const distanceM = user
    ? haversineM(user.lat, user.lng, place.lat, place.lng)
    : null;
  const walkMin = distanceM !== null ? tildeWalkMinutes(distanceM) : null;

  const openState = isOpenNow(place.opening_hours_periods);

  const handleTogglePin = async () => {
    if (pinning) return;
    setPinning(true);
    try {
      await onPin(!isPinned);
    } finally {
      setPinning(false);
    }
  };

  const handleTakeMeThere = () => {
    // For Phase 1C we hand off to Google Maps via the deep link
    // — same pattern as PlacesResultCard's "Open in Maps." This
    // lets the user use turn-by-turn outside the app. Phase 4
    // could wire up the in-app polyline navigation we already
    // have on MapPage, but that's a unification job — out of
    // scope for the map_exploration view itself.
    const url = `https://www.google.com/maps/dir/?api=1&destination=${place.lat},${place.lng}&destination_place_id=${encodeURIComponent(place.place_id)}&travelmode=walking`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          mb: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, lineHeight: 1.25, mb: 0.5 }}
          >
            {place.name}
          </Typography>
          {/* Rating / reviews / distance line */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}
          >
            {typeof place.rating === 'number' && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                }}
              >
                <StarRounded sx={{ fontSize: 16, color: '#f59e0b' }} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, fontSize: '0.78rem' }}
                >
                  {place.rating.toFixed(1)}
                </Typography>
                {typeof place.user_ratings_total === 'number' && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.72rem' }}
                  >
                    ({place.user_ratings_total.toLocaleString()})
                  </Typography>
                )}
              </Box>
            )}
            {distanceM !== null && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.72rem' }}
              >
                · {formatMeters(distanceM)}
                {walkMin !== null && ` · ~${walkMin} min walk`}
              </Typography>
            )}
            {/* Open-now indicator. Three states: open / closed /
                unknown. We show closed explicitly because it
                changes the user's decision. We hide the unknown
                state — too many places have missing hours data
                and "hours unknown" reads as broken. */}
            {openState === true && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                  color: 'success.dark',
                }}
              >
                <AccessTimeFilled sx={{ fontSize: 14 }} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, fontSize: '0.72rem' }}
                >
                  {t('Open now')}
                </Typography>
              </Box>
            )}
            {openState === false && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                  color: 'error.dark',
                }}
              >
                <DoNotDisturbAltOutlined sx={{ fontSize: 14 }} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, fontSize: '0.72rem' }}
                >
                  {t('Closed')}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label={t('Close')}
          sx={{ flexShrink: 0, mt: -0.5, mr: -0.5 }}
        >
          <CloseOutlined fontSize="small" />
        </IconButton>
      </Box>

      {/* AI guide blurb. The reason this place is on the map. */}
      {place.blurb && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.primary',
            lineHeight: 1.5,
            mb: 2,
            fontStyle: 'italic',
            // Subtle left rule to set the blurb apart as the AI's
            // contribution (vs. raw Google Places metadata above).
            borderLeft: 3,
            borderColor: 'primary.main',
            pl: 1.25,
          }}
        >
          {place.blurb}
        </Typography>
      )}

      {/* Action row. Pin-toggle + Take-me-there. Phase 4 will
          add an "Ask AI about this" button here too. */}
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Button
          variant={isPinned ? 'outlined' : 'contained'}
          startIcon={
            isPinned ? <PushPin fontSize="small" /> : <PushPinOutlined fontSize="small" />
          }
          onClick={handleTogglePin}
          disabled={pinning}
          sx={{ flex: '1 1 140px', textTransform: 'none' }}
        >
          {isPinned ? t('Pinned') : t('Pin to my map')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<DirectionsWalkOutlined fontSize="small" />}
          onClick={handleTakeMeThere}
          sx={{ flex: '1 1 140px', textTransform: 'none' }}
        >
          {t('Take me there')}
        </Button>
      </Stack>
    </Box>
  );
};
